import React, { useEffect, useMemo, useState } from 'react';
import type { Category } from '../../types/api-responses';
import { resolveImageUrl } from '../../utils/imageUrl';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './CategoryBrowseModal.module.css';

/**
 * CR-2026-05-15 — Browse master categories and subscribe with one click.
 *
 * Side-panel modal opened from My Business → Categories. Lists the active
 * master Category rows, lets the owner search, and exposes a single
 * "+ Subscribe" pill per row. Already-subscribed categories show a green
 * "✓ Added" disabled pill instead.
 *
 * The actual subscribe call lives in the parent (`onSubscribe`) — this
 * component is pure UI + intermediate "subscribing…" state for the row
 * being acted on. The parent owns BE coordination + list refresh.
 */
export interface CategoryBrowseModalProps {
    isOpen: boolean;
    onClose: () => void;
    master: Category[];
    subscribedIds: Set<number>;
    onSubscribe: (categoryId: number) => Promise<void>;
    loading?: boolean;
}

const CategoryBrowseModal: React.FC<CategoryBrowseModalProps> = ({
    isOpen,
    onClose,
    master,
    subscribedIds,
    onSubscribe,
    loading = false,
}) => {
    const { t, language } = useLanguage();
    const catName = (c: Category) => (language === 'ar' && c.nameAr ? c.nameAr : c.name);
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setBusyId(null);
        }
    }, [isOpen]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return master;
        return master.filter(c =>
            c.name.toLowerCase().includes(q) || (c.nameAr ?? '').includes(search.trim()),
        );
    }, [master, search]);

    const handleClick = async (id: number) => {
        if (subscribedIds.has(id) || busyId !== null) return;
        setBusyId(id);
        try {
            await onSubscribe(id);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                <div className={styles.header}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={loading || busyId !== null}
                        aria-label={t('mb.common.close')}
                    >
                        ×
                    </button>
                    <h2 className={styles.title}>{t('mb.categoryBrowse.title')}</h2>
                    <p className={styles.subtitle}>{t('mb.categoryBrowse.subtitle')}</p>
                </div>

                <div className={styles.searchWrap}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder={t('mb.categoryBrowse.search')}
                        aria-label={t('mb.categoryBrowse.search')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className={styles.list}>
                    {filtered.length === 0 ? (
                        <div className={styles.empty}>
                            {search.trim() ? (
                                <>
                                    {t('mb.categoryBrowse.noMatch').replace('{q}', search.trim())}
                                    <br />
                                    {t('mb.categoryBrowse.noMatchHint')}
                                </>
                            ) : (
                                <>{t('mb.categoryBrowse.loading')}</>
                            )}
                        </div>
                    ) : (
                        filtered.map(c => {
                            const added = subscribedIds.has(c.id);
                            const busy = busyId === c.id;
                            return (
                                <div key={c.id} className={styles.row}>
                                    <div className={styles.rowThumb}>
                                        {c.imageUrl && !brokenImages.has(c.id) ? (
                                            <img
                                                src={resolveImageUrl(c.imageUrl)}
                                                alt=""
                                                onError={() => setBrokenImages(prev => new Set(prev).add(c.id))}
                                            />
                                        ) : (
                                            <span aria-hidden="true">🏷️</span>
                                        )}
                                    </div>
                                    <div className={styles.rowMain}>
                                        <div className={styles.rowName}>{catName(c)}</div>
                                        <div className={styles.rowHint}>
                                            {added
                                                ? t('mb.categoryBrowse.already')
                                                : t('mb.categoryBrowse.hint')}
                                        </div>
                                    </div>
                                    <div className={styles.rowAction}>
                                        {added ? (
                                            <span className={styles.addedPill}>{t('mb.categoryBrowse.added')}</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.subscribeBtn}
                                                onClick={() => handleClick(c.id)}
                                                disabled={busy || busyId !== null}
                                            >
                                                {busy ? t('mb.categoryBrowse.adding') : t('mb.categoryBrowse.add')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className={styles.footer}>
                    {t('mb.categoryBrowse.footer')}
                </div>
            </div>
        </div>
    );
};

export default CategoryBrowseModal;
